import { useCallback, type ReactNode } from 'react'
import { Box, useTheme, type SxProps, type Theme } from '@mui/material'
import { motion, useMotionValue, useMotionTemplate, useSpring } from 'framer-motion'

interface Props {
  children: ReactNode
  /** When true, a very subtle slow halo breathes behind the card. */
  pulse?: boolean
  onClick?: () => void
  /** Right-click handler. Passes through the raw event so the caller
   *  can position a context menu at the cursor. */
  onContextMenu?: (e: React.MouseEvent) => void
  sx?: SxProps<Theme>
}

/**
 * A premium card with a neutral cursor-tracked spotlight on hover.
 * Deliberately monochromatic — priority colour is conveyed elsewhere,
 * not by the glow.
 */
export function SpotlightCard({ children, pulse = false, onClick, onContextMenu, sx }: Props) {
  const theme = useTheme()
  const isLight = theme.palette.mode === 'light'

  const mouseX = useMotionValue(-200)
  const mouseY = useMotionValue(-200)
  const smoothX = useSpring(mouseX, { stiffness: 220, damping: 30, mass: 0.6 })
  const smoothY = useSpring(mouseY, { stiffness: 220, damping: 30, mass: 0.6 })

  // Neutral tone: very soft black on light mode, soft white on dark mode
  const spotlight = isLight
    ? 'rgba(15, 23, 42, 0.05)'
    : 'rgba(255, 255, 255, 0.06)'
  const background = useMotionTemplate`radial-gradient(280px circle at ${smoothX}px ${smoothY}px, ${spotlight}, transparent 70%)`

  const haloColor = isLight ? 'rgba(15,23,42,0.06)' : 'rgba(255,255,255,0.06)'

  const handleMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect()
      mouseX.set(e.clientX - rect.left)
      mouseY.set(e.clientY - rect.top)
    },
    [mouseX, mouseY],
  )

  const handleLeave = useCallback(() => {
    mouseX.set(-200)
    mouseY.set(-200)
  }, [mouseX, mouseY])

  return (
    <Box
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onClick={onClick}
      onContextMenu={onContextMenu}
      sx={{
        position: 'relative',
        flexShrink: 0,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 180ms ease, transform 180ms ease, box-shadow 180ms ease',
        overflow: 'hidden',
        '&:hover': {
          borderColor: (t) =>
            t.palette.mode === 'light' ? 'rgba(0,0,0,0.14)' : 'rgba(255,255,255,0.14)',
          transform: 'translateY(-1px)',
          boxShadow: (t) =>
            t.palette.mode === 'light'
              ? '0 6px 20px -10px rgba(15,23,42,0.14)'
              : '0 6px 20px -10px rgba(0,0,0,0.5)',
        },
        ...sx,
      }}
    >
      {/* Very subtle pulse (critical + overdue items only) */}
      {pulse && (
        <motion.div
          animate={{ opacity: [0.25, 0.5, 0.25] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            inset: -1,
            borderRadius: 16,
            boxShadow: `0 0 0 1.5px ${haloColor}`,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}

      {/* Neutral cursor-tracked spotlight */}
      <motion.div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1 }}>{children}</Box>
    </Box>
  )
}
